import React, { useEffect, useState } from "react";
import { useLanguage } from "@/lib/i18n";
import { X } from "lucide-react";

const PROMPT_KEY = "wedo:lang:prompted";
const STORAGE_KEY = "wedo:lang";

/**
 * Suggests the user's browser language on first visit only.
 * - Shows a discreet banner if browser lang differs from current site lang
 * - Stores dismissal so it appears only once
 */
export default function LanguagePrompt() {
  const { lang, setLang } = useLanguage();
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const alreadyPrompted = window.localStorage.getItem(PROMPT_KEY);
    const alreadyChosen = window.localStorage.getItem(STORAGE_KEY);
    if (alreadyPrompted || alreadyChosen) return;

    const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
    const browserLang = nav.startsWith("fr") ? "fr" : "en";
    if (browserLang !== lang) setSuggested(browserLang);
  }, [lang]);

  const accept = () => {
    setLang(suggested);
    if (typeof window !== "undefined") window.localStorage.setItem(PROMPT_KEY, "1");
    setSuggested(null);
  };

  const dismiss = () => {
    if (typeof window !== "undefined") window.localStorage.setItem(PROMPT_KEY, "1");
    setSuggested(null);
  };

  if (!suggested) return null;

  const isFr = suggested === "fr";
  const message = isFr
    ? "Voir le site en français ?"
    : "View this site in English?";
  const accept_label = isFr ? "Passer en FR" : "Switch to EN";
  const dismiss_label = isFr ? "Rester en EN" : "Stay in FR";

  return (
    <div
      data-testid="language-prompt"
      role="dialog"
      aria-label="Language suggestion"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md fade-up"
    >
      <div className="bg-brand-deep text-white rounded-2xl shadow-2xl border border-white/15 backdrop-blur-md px-5 py-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="display italic text-[15px] md:text-base leading-tight">{message}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={accept}
            data-testid="lang-prompt-accept"
            className="text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase px-3 py-2 rounded-full bg-accent-pink text-white hover:bg-white hover:text-brand transition-colors"
          >
            {accept_label}
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="lang-prompt-dismiss"
            aria-label={dismiss_label}
            title={dismiss_label}
            className="text-white/85 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
